/**
 * Login Component - Modern Design
 * Main authentication component using composition pattern
 * Follows React conventions and accessibility guidelines
 */

import React from 'react';
import { Card, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLoginForm } from '../../hooks/useLoginForm';
import { useResponsiveDesign } from '../../hooks/useResponsiveDesign';
import { useAccessibility } from '../../hooks/useAccessibility';
import { LoginCredentials } from '../../types/auth.types';

// Sub-components
import LoginHeader from './LoginHeader';
import LoginForm from './LoginForm';
import LoginDivider from './LoginDivider';
import SSOButton from './SSOButton';

import styles from './Login.module.scss';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsiveDesign();
  const {
    loading,
    ssoLoading,
    handleLogin,
    handleSSOLogin,
    handleForgotPassword,
  } = useLoginForm();

  const { containerRef, announceToScreenReader } = useAccessibility({
    autoFocus: true,
    ariaLabel: 'Login form',
  });

  const handleFormSubmit = async (credentials: LoginCredentials): Promise<void> => {
    try {
      announceToScreenReader('Signing in...');
      await handleLogin(credentials);
      announceToScreenReader('Login successful! Redirecting...');
    } catch (error) {
      announceToScreenReader('Login failed. Please check your credentials and try again.');
      throw error;
    }
  };

  const handleSupportClick = (): void => {
    message.info('Please contact support for assistance.', 3);
  };

  return (
    <div 
      className={styles.loginContainer}
      role="main"
      aria-label="Login page"
    >
      <div className={styles.loginWrapper}>
        <Card 
          className={styles.loginCard}
          ref={containerRef}
          role="form"
          aria-labelledby="login-title"
          aria-describedby="login-description"
        >
          {/* Header Section with Logo and Title */}
          <LoginHeader 
            title="NAVI PORTAL"
            logoSrc="/images/logo-fpt.png"
            logoAlt="FPT Logo"
          />

          {/* Main Login Form */}
          <LoginForm 
            onSubmit={handleFormSubmit}
            loading={loading}
          />

          {/* Divider between login methods */}
          <LoginDivider 
            text="OR"
          />

          {/* SSO Authentication Button */}
          <SSOButton 
            onClick={handleSSOLogin}
            loading={ssoLoading}
          >
            SIGN IN WITH AZURE AD
          </SSOButton>
        </Card>
      </div>
    </div>
  );
};

Login.displayName = 'Login';

export default Login;
